import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, ArrowLeft, Check, Search, FileText, Settings, Link2, Globe, MapPin, ClipboardList, ShoppingCart, ArrowLeftRight, CheckCircle2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/layout/Breadcrumbs';
import { getPaths, resolveServiceSlug } from '../utils/paths';

export default function ServiceDetail() {
  const { serviceId: rawServiceId } = useParams();
  const { language } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';
  const paths = getPaths(language);
  const serviceId = resolveServiceSlug(rawServiceId);

  const allServices = {
    'seo-strategie': {
      icon: <Search className="h-6 w-6" />,
      title: language === 'nl' ? 'SEO Strategie' : 'SEO Strategy',
      subtitle: language === 'nl'
        ? 'Data-gedreven SEO-strategieën die daadwerkelijk werken'
        : 'Data-driven SEO strategies that actually work',
      description: language === 'nl'
        ? 'Als freelance SEO specialist ontwikkel ik een op maat gemaakte SEO-strategie die aansluit op jouw business doelen. Geen standaard playbook, maar een strategische aanpak gebaseerd op data, concurrentieanalyse en jouw unieke marktpositie.'
        : 'As a freelance SEO specialist, I develop a bespoke SEO strategy tailored to your business goals. No standard playbook, but a strategic approach based on data, competitor analysis and your unique market position.',
      features: language === 'nl' ? [
        'Diepgaand zoekwoordenonderzoek en clustering',
        'Concurrentieanalyse en kansen identificatie',
        'Strategische roadmap met geprioriteerde acties',
        'Maandelijkse rapportage en bijsturing',
        'ROI-gedreven aanpak met meetbare KPI\'s',
        'Integratie met bestaande marketing activiteiten'
      ] : [
        'In-depth keyword research and clustering',
        'Competitor analysis and opportunity identification',
        'Strategic roadmap with prioritized actions',
        'Monthly reporting and adjustments',
        'ROI-driven approach with measurable KPIs',
        'Integration with existing marketing activities'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Vrijblijvende Kennismaking', desc: 'Een digitale kennismaking om jouw bedrijf, ambities en resources te bespreken.' },
        { step: '02', title: 'Strategisch Plan', desc: 'Een audit op basis van de 3 SEO-pijlers en de huidige status van je website.' },
        { step: '03', title: 'SEO Executiefase', desc: 'Hier wordt de strategie uitgevoerd die uitgewerkt is in het strategisch plan.' },
        { step: '04', title: 'Onderhoudsfase', desc: 'In deze fase wordt alles onderhouden en bijgestuurd.' }
      ] : [
        { step: '01', title: 'Free Introduction', desc: 'A digital introduction to discuss your business, ambitions and resources.' },
        { step: '02', title: 'Strategic Plan', desc: 'An audit based on the 3 SEO pillars and your website\'s current status.' },
        { step: '03', title: 'SEO Execution Phase', desc: 'This is where the strategy from the strategic plan is executed.' },
        { step: '04', title: 'Maintenance Phase', desc: 'In this phase, everything is maintained and adjusted.' }
      ]
    },
    'seo-tekstschrijver': {
      icon: <FileText className="h-6 w-6" />,
      title: language === 'nl' ? 'SEO Teksten' : 'SEO Copywriting',
      subtitle: language === 'nl'
        ? 'Content die rankt én converteert'
        : 'Content that ranks and converts',
      description: language === 'nl'
        ? 'Goede SEO-teksten zijn geen kunst, maar wetenschap. Ze combineren zoekwoordenonderzoek met conversie-optimalisatie om bezoekers te trekken én te overtuigen. Van landingspagina\'s tot blogartikelen — ik schrijf content die aansluit bij de zoekintentie van jouw doelgroep.'
        : 'Good SEO copy is not art, but science. It combines keyword research with conversion optimization to attract and convince visitors. From landing pages to blog articles — I write content that matches your target audience\'s search intent.',
      features: language === 'nl' ? [
        'Zoekwoordenonderzoek en content mapping',
        'SEO-geoptimaliseerde landingspagina\'s',
        'Blogartikelen en pillar content',
        'Product- en categoriebeschrijvingen',
        'Meta titles en descriptions',
        'Content refresh en updates'
      ] : [
        'Keyword research and content mapping',
        'SEO-optimized landing pages',
        'Blog articles and pillar content',
        'Product and category descriptions',
        'Meta titles and descriptions',
        'Content refresh and updates'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Zoekwoordenonderzoek', desc: 'Analyse van zoekintentie en content kansen.' },
        { step: '02', title: 'Content strategie', desc: 'Planning op basis van customer journey.' },
        { step: '03', title: 'Schrijven', desc: 'Optimaliseren voor zoekmachines én lezers.' },
        { step: '04', title: 'Publicatie', desc: 'Monitoring van rankings en conversies.' }
      ] : [
        { step: '01', title: 'Keyword research', desc: 'Analysis of search intent and content opportunities.' },
        { step: '02', title: 'Content strategy', desc: 'Planning based on customer journey.' },
        { step: '03', title: 'Writing', desc: 'Optimizing for search engines and readers.' },
        { step: '04', title: 'Publication', desc: 'Monitoring rankings and conversions.' }
      ]
    },
    'technische-seo': {
      icon: <Settings className="h-6 w-6" />,
      title: language === 'nl' ? 'Technische SEO' : 'Technical SEO',
      subtitle: language === 'nl'
        ? 'De technische basis voor organische groei'
        : 'The technical foundation for organic growth',
      description: language === 'nl'
        ? 'Technische SEO is het fundament van elke succesvolle SEO-strategie. Van Core Web Vitals tot crawlability — ik zorg ervoor dat zoekmachines je site efficiënt kunnen crawlen en indexeren. Diepgaande audits die elke stap concreet en uitvoerbaar maken.'
        : 'Technical SEO is the foundation of every successful SEO strategy. From Core Web Vitals to crawlability — I ensure search engines can efficiently crawl and index your site. In-depth audits that are actionable every step of the way.',
      features: language === 'nl' ? [
        'Core Web Vitals optimalisatie',
        'Crawlability & indexatie analyse',
        'Site architectuur optimalisatie',
        'Schema markup implementatie',
        'XML sitemap & robots.txt',
        'Internationaal hreflang setup'
      ] : [
        'Core Web Vitals optimization',
        'Crawlability & indexation analysis',
        'Site architecture optimization',
        'Schema markup implementation',
        'XML sitemap & robots.txt',
        'International hreflang setup'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Technische audit', desc: 'Volledige scan van alle technische aspecten.' },
        { step: '02', title: 'Prioritering', desc: 'Actiepunten rangschikken op impact.' },
        { step: '03', title: 'Implementatie', desc: 'Samenwerking met development team.' },
        { step: '04', title: 'Validatie', desc: 'Controleren of alle fixes correct zijn doorgevoerd.' }
      ] : [
        { step: '01', title: 'Technical audit', desc: 'Full scan of all technical aspects.' },
        { step: '02', title: 'Prioritization', desc: 'Ranking action items by impact.' },
        { step: '03', title: 'Implementation', desc: 'Collaboration with development team.' },
        { step: '04', title: 'Validation', desc: 'Verifying all fixes are correctly implemented.' }
      ]
    },
    'linkbuilding': {
      icon: <Link2 className="h-6 w-6" />,
      title: language === 'nl' ? 'Linkbuilding' : 'Link Building',
      subtitle: language === 'nl'
        ? 'Autoriteit bouwen die blijft'
        : 'Building lasting authority',
      description: language === 'nl'
        ? 'Linkbuilding draait niet om kwantiteit, maar om relevantie en kwaliteit. Een realistische linkbuilding strategie op basis van concurrentieanalyse, met een helder budget om te ranken op jouw producten en diensten. Strategische backlinks van betrouwbare bronnen verhogen je domeinautoriteit.'
        : 'Link building is not about quantity, but relevance and quality. A realistic link building strategy based on competitor analysis, with a clear budget to rank for your products and services. Strategic backlinks from trusted sources increase your domain authority.',
      features: language === 'nl' ? [
        'Backlinkprofiel analyse',
        'Competitor link gap analyse',
        'Digital PR en outreach',
        'Guest posting strategie',
        'Broken link building',
        'Link monitoring en disavow'
      ] : [
        'Backlink profile analysis',
        'Competitor link gap analysis',
        'Digital PR and outreach',
        'Guest posting strategy',
        'Broken link building',
        'Link monitoring and disavow'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Analyse', desc: 'Huidige backlinkprofiel en concurrenten in kaart brengen.' },
        { step: '02', title: 'Strategie', desc: 'Link opportunities identificeren en plan maken.' },
        { step: '03', title: 'Outreach', desc: 'Relaties opbouwen en links verwerven.' },
        { step: '04', title: 'Monitoring', desc: 'Resultaten meten en bijsturen.' }
      ] : [
        { step: '01', title: 'Analysis', desc: 'Map current backlink profile and competitors.' },
        { step: '02', title: 'Strategy', desc: 'Identify link opportunities and create plan.' },
        { step: '03', title: 'Outreach', desc: 'Build relationships and acquire links.' },
        { step: '04', title: 'Monitoring', desc: 'Measure results and adjust.' }
      ]
    },
    'seo-consultancy': {
      icon: <Globe className="h-6 w-6" />,
      title: 'SEO Consultancy',
      subtitle: language === 'nl'
        ? 'Strategisch advies op het hoogste niveau'
        : 'Strategic advice at the highest level',
      description: language === 'nl'
        ? 'Heb je al een SEO-strategie en wil je een second opinion? Of heb je een in-house team dat strategische sturing nodig heeft? Ik kijk graag mee en adviseer op strategisch niveau.'
        : 'Already have an SEO strategy and want a second opinion? Or do you have an in-house team that needs strategic guidance? I am happy to review and advise at a strategic level.',
      features: language === 'nl' ? [
        'Strategische SEO review',
        'Second opinion op huidige aanpak',
        'Roadmap review en bijsturing',
        'Stakeholder presentaties',
        'Ad hoc advies en sparring'
      ] : [
        'Strategic SEO review',
        'Second opinion on current approach',
        'Roadmap review and adjustments',
        'Stakeholder presentations',
        'Ad hoc advice and sparring'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Intake', desc: 'Huidige situatie, team en doelen in kaart brengen.' },
        { step: '02', title: 'Review', desc: 'Analyse van strategie, uitvoering en resultaten.' },
        { step: '03', title: 'Advies', desc: 'Concrete aanbevelingen en prioriteiten.' },
        { step: '04', title: 'Begeleiding', desc: 'Ongoing sparring en coaching.' }
      ] : [
        { step: '01', title: 'Intake', desc: 'Map current situation, team and goals.' },
        { step: '02', title: 'Review', desc: 'Analysis of strategy, execution and results.' },
        { step: '03', title: 'Advice', desc: 'Concrete recommendations and priorities.' },
        { step: '04', title: 'Guidance', desc: 'Ongoing sparring and coaching.' }
      ]
    },
    'geo': {
      icon: <Globe className="h-6 w-6" />,
      title: 'GEO',
      subtitle: language === 'nl'
        ? 'Vindbaar worden in AI-driven zoekresultaten'
        : 'Get found in AI-driven search results',
      description: language === 'nl'
        ? 'Generative Engine Optimization (GEO) is de volgende stap in zoekmachineoptimalisatie. Met de opkomst van AI-driven zoekresultaten is het essentieel om zichtbaar te zijn in tools als ChatGPT, Perplexity en Google AI Overviews. Ik help je om je LLM-zichtbaarheid te vergroten.'
        : 'Generative Engine Optimization (GEO) is the next step in search engine optimization. With the rise of AI-driven search results, it\'s essential to be visible in tools like ChatGPT, Perplexity and Google AI Overviews. I help you increase your LLM visibility.',
      features: language === 'nl' ? [
        'LLM-zichtbaarheid analyse',
        'AI Overviews optimalisatie',
        'Structured data voor AI',
        'Content optimalisatie voor LLMs',
        'Brand mention strategie',
        'Monitoring AI-zoekresultaten'
      ] : [
        'LLM visibility analysis',
        'AI Overviews optimization',
        'Structured data for AI',
        'Content optimization for LLMs',
        'Brand mention strategy',
        'Monitoring AI search results'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Analyse', desc: 'Huidige LLM-zichtbaarheid in kaart brengen.' },
        { step: '02', title: 'Strategie', desc: 'Plan voor AI-zoekresultaat optimalisatie.' },
        { step: '03', title: 'Optimalisatie', desc: 'Content en data aanpassen voor LLMs.' },
        { step: '04', title: 'Monitoring', desc: 'Resultaten tracken in AI-zoekomgevingen.' }
      ] : [
        { step: '01', title: 'Analysis', desc: 'Map current LLM visibility.' },
        { step: '02', title: 'Strategy', desc: 'Plan for AI search result optimization.' },
        { step: '03', title: 'Optimization', desc: 'Adjust content and data for LLMs.' },
        { step: '04', title: 'Monitoring', desc: 'Track results in AI search environments.' }
      ]
    },
    'local-seo': {
      icon: <MapPin className="h-6 w-6" />,
      title: language === 'nl' ? 'Lokale SEO' : 'Local SEO',
      subtitle: language === 'nl'
        ? 'Domineer je lokale markt'
        : 'Dominate your local market',
      description: language === 'nl'
        ? 'Voor bedrijven met een fysieke locatie is Local SEO essentieel. Van Google Business Profile optimalisatie tot lokale citations — ik zorg ervoor dat jouw bedrijf bovenaan staat in lokale zoekresultaten. Word de nummer één in je regio.'
        : 'For businesses with a physical location, Local SEO is essential. From Google Business Profile optimization to local citations — I ensure your business is at the top of local search results. Become number one in your region.',
      features: language === 'nl' ? [
        'Google Business Profile optimalisatie',
        'Lokale zoekwoorden strategie',
        'NAP consistentie check',
        'Lokale citations building',
        'Review management strategie',
        'Local schema markup'
      ] : [
        'Google Business Profile optimization',
        'Local keyword strategy',
        'NAP consistency check',
        'Local citations building',
        'Review management strategy',
        'Local schema markup'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Local audit', desc: 'Huidige lokale zichtbaarheid analyseren.' },
        { step: '02', title: 'GBP optimalisatie', desc: 'Google Business Profile volledig optimaliseren.' },
        { step: '03', title: 'Citations', desc: 'Lokale vermeldingen opbouwen en corrigeren.' },
        { step: '04', title: 'Monitoring', desc: 'Lokale rankings en reviews monitoren.' }
      ] : [
        { step: '01', title: 'Local audit', desc: 'Analyze current local visibility.' },
        { step: '02', title: 'GBP optimization', desc: 'Fully optimize Google Business Profile.' },
        { step: '03', title: 'Citations', desc: 'Build and correct local listings.' },
        { step: '04', title: 'Monitoring', desc: 'Monitor local rankings and reviews.' }
      ]
    },
    'seo-audits': {
      icon: <ClipboardList className="h-6 w-6" />,
      title: 'SEO Audits',
      subtitle: language === 'nl'
        ? 'Een diepgaande analyse van je gehele website'
        : 'An in-depth analysis of your entire website',
      description: language === 'nl'
        ? 'Een SEO-audit is het startpunt van elke succesvolle strategie. Ik analyseer je website op technisch, content en autoriteit vlak en lever een concreet, uitvoerbaar en geprioriteerd actieplan op. Geen generieke rapporten, maar maatwerk met focus op impact.'
        : 'An SEO audit is the starting point of every successful strategy. I analyze your website on technical, content and authority level and deliver a concrete, actionable and prioritized action plan. No generic reports, but bespoke work focused on impact.',
      features: language === 'nl' ? [
        'Technische website analyse',
        'Content kwaliteit en gaps',
        'Backlinkprofiel beoordeling',
        'Concurrentie benchmark',
        'Geprioriteerd actieplan',
        'Presentatie met aanbevelingen'
      ] : [
        'Technical website analysis',
        'Content quality and gaps',
        'Backlink profile assessment',
        'Competitor benchmark',
        'Prioritized action plan',
        'Presentation with recommendations'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Data verzameling', desc: 'Crawl, analytics en Search Console data analyseren.' },
        { step: '02', title: 'Analyse', desc: 'Techniek, content en autoriteit doorlichten.' },
        { step: '03', title: 'Rapportage', desc: 'Concreet actieplan met prioriteiten opstellen.' },
        { step: '04', title: 'Presentatie', desc: 'Bevindingen en aanbevelingen bespreken.' }
      ] : [
        { step: '01', title: 'Data collection', desc: 'Analyze crawl, analytics and Search Console data.' },
        { step: '02', title: 'Analysis', desc: 'Review technical, content and authority aspects.' },
        { step: '03', title: 'Reporting', desc: 'Create concrete action plan with priorities.' },
        { step: '04', title: 'Presentation', desc: 'Discuss findings and recommendations.' }
      ]
    },
    'ecommerce-seo': {
      icon: <ShoppingCart className="h-6 w-6" />,
      title: 'Ecommerce SEO',
      subtitle: language === 'nl'
        ? 'Meer organisch verkeer naar je webshop'
        : 'More organic traffic to your webshop',
      description: language === 'nl'
        ? 'Ecommerce SEO vereist een specifieke aanpak. Van categoriepagina\'s tot productpagina\'s — ik optimaliseer je webshop voor zoekintentie én conversie. Met ervaring bij merken als Alpina.nl weet ik wat werkt.'
        : 'Ecommerce SEO requires a specific approach. From category pages to product pages — I optimize your webshop for search intent and conversion. With experience at brands like Alpina.nl, I know what works.',
      highlights: language === 'nl' ? [
        { title: 'Purchase fase eerst', desc: 'De hoofdfocus ligt altijd op de koopintentie — daar wordt het geld verdiend.' },
        { title: 'Concurrend genoeg?', desc: 'Ik analyseer je productaanbod, pricing en filters vs. de concurrentie. Als jij 1 product aanbiedt en de concurrent 100 — dat is een probleem.' },
        { title: 'Gebruikersgedrag is king', desc: 'SEO draait niet alleen om rankings. Als bezoekers niet converteren, mis je omzet — ongeacht je posities.' },
      ] : [
        { title: 'Purchase phase first', desc: 'The main focus is always on purchase intent — that is where the money is made.' },
        { title: 'Competitive enough?', desc: 'I analyze your product offering, pricing and filters vs. the competition. If you offer 1 product and your competitor offers 100 — that is a problem.' },
        { title: 'User behaviour is king', desc: 'SEO is not just about rankings. If visitors don\'t convert, you\'re missing revenue — regardless of your positions.' },
      ],
      features: language === 'nl' ? [
        'Categoriepagina optimalisatie',
        'Product SEO en schema markup',
        'Interne linkstructuur',
        'Faceted navigation oplossingen',
        'Content strategie voor e-commerce',
        'Conversie-optimalisatie',
        'Customer journey analyse',
        'Concurrentie & productaanbod check'
      ] : [
        'Category page optimization',
        'Product SEO and schema markup',
        'Internal link structure',
        'Faceted navigation solutions',
        'Content strategy for e-commerce',
        'Conversion optimization',
        'Customer journey analysis',
        'Competitor & product offering check'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Shop audit', desc: 'Technische en content analyse van je webshop.' },
        { step: '02', title: 'Keyword mapping', desc: 'Zoekwoorden koppelen aan categorieën en producten.' },
        { step: '03', title: 'Optimalisatie', desc: 'Categorie-, product- en filterpagina\'s verbeteren.' },
        { step: '04', title: 'Groei', desc: 'Content uitbreiden en autoriteit opbouwen.' }
      ] : [
        { step: '01', title: 'Shop audit', desc: 'Technical and content analysis of your webshop.' },
        { step: '02', title: 'Keyword mapping', desc: 'Map keywords to categories and products.' },
        { step: '03', title: 'Optimization', desc: 'Improve category, product and filter pages.' },
        { step: '04', title: 'Growth', desc: 'Expand content and build authority.' }
      ]
    },
    'migratie': {
      icon: <ArrowLeftRight className="h-6 w-6" />,
      title: language === 'nl' ? 'Migraties' : 'Migrations',
      subtitle: language === 'nl'
        ? 'Veilig verhuizen zonder traffic verlies'
        : 'Safe migration without traffic loss',
      description: language === 'nl'
        ? 'Site migraties zijn risicovol als ze niet goed worden uitgevoerd. Met de juiste planning, redirect mapping en monitoring behoud je je rankings en traffic tijdens en na de migratie. Met ervaring bij complexe migraties voor merken als JDE en CitizenM weet ik precies waar de risico\'s zitten.'
        : 'Site migrations are risky if not executed properly. With proper planning, redirect mapping and monitoring, you maintain your rankings and traffic during and after the migration. With experience in complex migrations for brands like JDE and CitizenM, I know exactly where the risks lie.',
      features: language === 'nl' ? [
        'Pre-migratie audit en risico analyse',
        'URL mapping en redirect planning',
        'Content migratie begeleiding',
        'Technische migratie checklist',
        'Post-migratie monitoring',
        'Ranking recovery support'
      ] : [
        'Pre-migration audit and risk analysis',
        'URL mapping and redirect planning',
        'Content migration guidance',
        'Technical migration checklist',
        'Post-migration monitoring',
        'Ranking recovery support'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Pre-migratie audit', desc: 'Huidige site volledig documenteren.' },
        { step: '02', title: 'Redirect mapping', desc: 'Alle URL\'s 1-op-1 mappen naar nieuwe structuur.' },
        { step: '03', title: 'Go-live', desc: 'Begeleiding tijdens de migratie zelf.' },
        { step: '04', title: 'Post-migratie', desc: 'Monitoring en snelle actie bij issues.' }
      ] : [
        { step: '01', title: 'Pre-migration audit', desc: 'Fully document current site.' },
        { step: '02', title: 'Redirect mapping', desc: 'Map all URLs 1-to-1 to new structure.' },
        { step: '03', title: 'Go-live', desc: 'Guidance during the migration itself.' },
        { step: '04', title: 'Post-migration', desc: 'Monitoring and quick action on issues.' }
      ]
    },
    'traffic-recovery-audit': {
      icon: <Search className="h-6 w-6" />,
      title: 'Traffic Recovery Audit',
      subtitle: language === 'nl'
        ? 'Herstel je posities na een Google-update'
        : 'Recover your positions after a Google update',
      description: language === 'nl'
        ? 'Heeft je website een daling meegemaakt na een Google-update of penalty? Ik analyseer de exacte oorzaak — van thin content tot link quality — en stel een concreet herstelplan op om je posities en traffic terug te winnen.'
        : 'Has your website experienced a drop after a Google update or penalty? I analyse the exact cause — from thin content to link quality — and create a concrete recovery plan to regain your positions and traffic.',
      features: language === 'nl' ? [
        'Google update impact analyse',
        'Technische en content audit',
        'Link profiel beoordeling',
        'Herstelstrategie met prioriteiten',
        'Monitoring van recovery voortgang',
        'Preventieve aanbevelingen'
      ] : [
        'Google update impact analysis',
        'Technical and content audit',
        'Link profile assessment',
        'Recovery strategy with priorities',
        'Recovery progress monitoring',
        'Preventive recommendations'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Impact analyse', desc: 'Vaststellen welke update en welke pagina\'s getroffen zijn.' },
        { step: '02', title: 'Root cause', desc: 'Diepgaande analyse van de onderliggende oorzaak.' },
        { step: '03', title: 'Herstelplan', desc: 'Concreet actieplan met geprioriteerde stappen.' },
        { step: '04', title: 'Monitoring', desc: 'Recovery tracking en bijsturing waar nodig.' }
      ] : [
        { step: '01', title: 'Impact analysis', desc: 'Determine which update and which pages were affected.' },
        { step: '02', title: 'Root cause', desc: 'In-depth analysis of the underlying cause.' },
        { step: '03', title: 'Recovery plan', desc: 'Concrete action plan with prioritised steps.' },
        { step: '04', title: 'Monitoring', desc: 'Recovery tracking and adjustments where needed.' }
      ]
    },
    'content-performance-audit': {
      icon: <FileText className="h-6 w-6" />,
      title: 'Content Performance Audit',
      subtitle: language === 'nl'
        ? 'Ontdek wat werkt en waar de kansen liggen'
        : 'Discover what works and where the opportunities are',
      description: language === 'nl'
        ? 'Een grondige analyse van je bestaande content: wat presteert goed, wat kan beter, wat moet weg en waar liggen onbenutte kansen. De basis voor een effectieve contentstrategie die aansluit bij zoekintentie en je business doelen.'
        : 'A thorough analysis of your existing content: what performs well, what can be improved, what should go and where are untapped opportunities. The foundation for an effective content strategy aligned with search intent and your business goals.',
      features: language === 'nl' ? [
        'Content inventarisatie en classificatie',
        'Zoekintentie mapping',
        'Content gap analyse',
        'Pruning aanbevelingen',
        'Content cluster planning',
        'Quick wins identificatie'
      ] : [
        'Content inventory and classification',
        'Search intent mapping',
        'Content gap analysis',
        'Pruning recommendations',
        'Content cluster planning',
        'Quick wins identification'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Inventarisatie', desc: 'Alle content crawlen en classificeren.' },
        { step: '02', title: 'Performance analyse', desc: 'Data-gedreven beoordeling van elke pagina.' },
        { step: '03', title: 'Kansen rapport', desc: 'Content gaps en quick wins identificeren.' },
        { step: '04', title: 'Strategie', desc: 'Actieplan voor content optimalisatie en creatie.' }
      ] : [
        { step: '01', title: 'Inventory', desc: 'Crawl and classify all content.' },
        { step: '02', title: 'Performance analysis', desc: 'Data-driven assessment of each page.' },
        { step: '03', title: 'Opportunities report', desc: 'Identify content gaps and quick wins.' },
        { step: '04', title: 'Strategy', desc: 'Action plan for content optimization and creation.' }
      ]
    },
    'full-scope-audit': {
      icon: <ClipboardList className="h-6 w-6" />,
      title: 'Full Scope Audit',
      subtitle: language === 'nl'
        ? 'Complete SEO-doorlichting op alle 3 pijlers'
        : 'Complete SEO review across all 3 pillars',
      description: language === 'nl'
        ? 'Een volledige SEO-audit die alle 3 de pijlers doorlicht: technische excellentie, content en autoriteit. Je ontvangt een concreet rapport met geprioriteerde aanbevelingen, zodat je precies weet waar de grootste impact te behalen is.'
        : 'A complete SEO audit that reviews all 3 pillars: technical excellence, content and authority. You receive a concrete report with prioritised recommendations, so you know exactly where the biggest impact can be achieved.',
      features: language === 'nl' ? [
        'Technische SEO audit',
        'Content analyse en gap assessment',
        'Link profiel en autoriteitsanalyse',
        'Concurrentie benchmark',
        'Geprioriteerd actieplan',
        'Strategische aanbevelingen'
      ] : [
        'Technical SEO audit',
        'Content analysis and gap assessment',
        'Link profile and authority analysis',
        'Competitive benchmark',
        'Prioritised action plan',
        'Strategic recommendations'
      ],
      process: language === 'nl' ? [
        { step: '01', title: 'Techniek', desc: 'Crawl, indexatie, snelheid en architectuur analyseren.' },
        { step: '02', title: 'Content', desc: 'Content inventaris, gaps en zoekintentie mapping.' },
        { step: '03', title: 'Autoriteit', desc: 'Link profiel analyse en kansen identificatie.' },
        { step: '04', title: 'Rapport', desc: 'Geprioriteerd actieplan met concrete stappen.' }
      ] : [
        { step: '01', title: 'Technical', desc: 'Crawl, indexation, speed and architecture analysis.' },
        { step: '02', title: 'Content', desc: 'Content inventory, gaps and search intent mapping.' },
        { step: '03', title: 'Authority', desc: 'Link profile analysis and opportunity identification.' },
        { step: '04', title: 'Report', desc: 'Prioritised action plan with concrete steps.' }
      ]
    }
  };

  const service = allServices[serviceId];

  if (!service) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#08080A] mb-4">
            {language === 'nl' ? 'Dienst niet gevonden' : 'Service not found'}
          </h1>
          <Link to={paths.diensten} className="inline-flex items-center gap-2 text-[#4286F4] font-semibold hover:underline">
            <ArrowLeft className="h-4 w-4" />
            {language === 'nl' ? 'Terug naar diensten' : 'Back to services'}
          </Link>
        </div>
      </div>
    );
  }

  // Get other services for the "other services" section
  const otherServices = Object.entries(allServices)
    .filter(([id]) => id !== serviceId)
    .slice(0, 3)
    .map(([id, s]) => ({ id, title: s.title, icon: s.icon }));

  return (
    <div data-testid={`service-detail-${serviceId}`}>
      <SEOHead
        title={`${service.title} | Kesult - Freelance SEO Specialist`}
        description={service.description}
        canonical={`https://www.kesult.com${paths.service(serviceId)}`}
        lang={language}
      />
      {/* Hero - Dark angled section */}
      <section className="relative bg-[#0B1A2E] text-white overflow-hidden" data-testid="service-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-32 lg:pb-40 relative z-10">
          <Link
            to={paths.diensten}
            className="inline-flex items-center gap-2 text-white/50 hover:text-white/80 transition-colors mb-8 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'nl' ? 'Alle diensten' : 'All services'}
          </Link>

          <div className="max-w-3xl">
            <div className="w-12 h-12 bg-[#4286F4]/20 rounded-xl flex items-center justify-center text-[#4286F4] mb-6">
              {service.icon}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight mb-4" data-testid="service-title">
              {service.title}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
              {service.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`${basePath}/contact`}>
                <button className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2" data-testid="service-cta-primary">
                  {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to={paths.werkwijze}>
                <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                  {language === 'nl' ? 'Bekijk werkwijze' : 'View process'}
                </button>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      <Breadcrumbs />

      {/* Description + Features */}
      <section className="py-20 lg:py-28 bg-white" data-testid="service-details">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
                {language === 'nl' ? 'Over deze dienst' : 'About this service'}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
                {service.title}
              </h2>
              <p className="text-[#555] leading-relaxed">
                {service.description}
              </p>

              {service.highlights && (
                <div className="mt-8 space-y-4">
                  {service.highlights.map((h, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-[#4286F4]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="h-4 w-4 text-[#4286F4]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#08080A]">{h.title}</p>
                        <p className="text-[#555] text-sm mt-0.5">{h.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
                {language === 'nl' ? 'Wat is inbegrepen' : "What's included"}
              </p>
              <ul className="space-y-4">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#4286F4] flex-shrink-0 mt-0.5" />
                    <span className="text-[#333]">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="service-process">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Het proces' : 'The process'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-14">
            {language === 'nl' ? 'Hoe ik te werk ga' : 'How I work'}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((item) => (
              <div key={item.step} className="group">
                <span className="text-3xl font-bold text-[#4286F4] block mb-3">{item.step}</span>
                <div className="h-px w-full bg-gray-300 group-hover:bg-[#4286F4] transition-colors mb-4" />
                <h3 className="text-base font-bold text-[#08080A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Linkbuilding Proof Section */}
      {serviceId === 'linkbuilding' && (
        <section className="py-20 lg:py-28 bg-white" data-testid="linkbuilding-proof">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Bewezen resultaten' : 'Proven results'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-4">
              {language === 'nl' ? 'Linkbuilding in de praktijk' : 'Link building in practice'}
            </h2>
            <p className="text-[#555] text-lg leading-relaxed max-w-3xl mb-12">
              {language === 'nl'
                ? 'Gerichte linkbuilding naar de juiste pagina\'s levert meetbare resultaten op. Hieronder twee voorbeelden waar het opbouwen van verwijzende domeinen direct heeft geleid tot meer organisch verkeer — bevestigd door zowel Ahrefs als Google Search Console.'
                : 'Targeted link building to the right pages delivers measurable results. Below are two examples where building referring domains directly led to more organic traffic — confirmed by both Ahrefs and Google Search Console.'}
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div className="rounded-2xl border border-gray-100 overflow-hidden bg-[#FAFAFA]">
                <div className="p-5 border-b border-gray-100">
                  <span className="bg-[#4286F4]/10 text-[#4286F4] text-xs font-bold px-2.5 py-1 rounded-full">Ahrefs</span>
                  <p className="text-[#555] text-sm mt-2">
                    {language === 'nl'
                      ? 'Verwijzende domeinen en organisch verkeer stijgen gelijktijdig'
                      : 'Referring domains and organic traffic rising simultaneously'}
                  </p>
                </div>
                <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/e7acpwvf_Foto_op_canvas_Ahrefs.png" alt="Ahrefs linkbuilding result" className="w-full" loading="lazy" />
              </div>
              <div className="rounded-2xl border border-gray-100 overflow-hidden bg-[#FAFAFA]">
                <div className="p-5 border-b border-gray-100">
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">Google Search Console</span>
                  <p className="text-[#555] text-sm mt-2">
                    {language === 'nl'
                      ? '673 clicks (+104%) en 115K impressies (+135%) vs. dezelfde periode vorig jaar'
                      : '673 clicks (+104%) and 115K impressions (+135%) vs. same period last year'}
                  </p>
                </div>
                <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/z94iamzv_Foto_op_canvas_Google_search_console.png" alt="Google Search Console verification" className="w-full" loading="lazy" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="rounded-2xl border border-gray-100 overflow-hidden bg-[#FAFAFA]">
                <div className="p-5 border-b border-gray-100">
                  <span className="bg-[#4286F4]/10 text-[#4286F4] text-xs font-bold px-2.5 py-1 rounded-full">Ahrefs</span>
                  <p className="text-[#555] text-sm mt-2">
                    {language === 'nl'
                      ? 'Zelfde patroon: meer verwijzende domeinen resulteren in meer organisch verkeer'
                      : 'Same pattern: more referring domains result in more organic traffic'}
                  </p>
                </div>
                <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/tdkqrd8f_foto_op_plexiglas_Ahrefs.png" alt="Ahrefs linkbuilding result example 2" className="w-full" loading="lazy" />
              </div>
              <div className="rounded-2xl border border-gray-100 overflow-hidden bg-[#FAFAFA]">
                <div className="p-5 border-b border-gray-100">
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">Google Search Console</span>
                  <p className="text-[#555] text-sm mt-2">
                    {language === 'nl'
                      ? '564 clicks (+134%) en 74.4K impressies (+140%) vs. dezelfde periode vorig jaar'
                      : '564 clicks (+134%) and 74.4K impressions (+140%) vs. same period last year'}
                  </p>
                </div>
                <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/9se5g57h_foto_op_plexiglas_Google_search_console.png" alt="Google Search Console verification example 2" className="w-full" loading="lazy" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Andere diensten' : 'Other services'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-10">
            {language === 'nl' ? 'Bekijk ook' : 'Also check out'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherServices.map((s) => (
              <Link
                key={s.id}
                to={paths.service(s.id)}
                className="group p-8 rounded-2xl border border-gray-100 hover:shadow-lg hover:border-[#4286F4]/20 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-[#4286F4]/10 rounded-xl flex items-center justify-center text-[#4286F4] mb-4">
                  {s.icon}
                </div>
                <h3 className="text-lg font-bold text-[#08080A] group-hover:text-[#4286F4] transition-colors mb-2">{s.title}</h3>
                <span className="inline-flex items-center gap-2 text-[#4286F4] font-semibold text-sm group-hover:gap-3 transition-all">
                  {language === 'nl' ? 'Meer info' : 'Find out more'}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#4286F4] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Klaar om te starten?' : 'Ready to get started?'}
          </h2>
          <div className="flex gap-4">
            <Link to={`${basePath}/contact`}>
              <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <a href="mailto:info@kesult.com">
              <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                {language === 'nl' ? 'Mail me' : 'Email me'}
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
